import { useState, useRef, useEffect } from 'react'
import { Handle, Position } from 'reactflow'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/ui/avatar'
import { getModelIconAndName } from '@/components/shared/model-info'
import { CheckCircle2, Maximize2, Copy, Database, FileText, ChevronDown, Loader2, RotateCw, Brain } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { MessageDialog } from '../ui/message-dialog'
import { ThoughtBubble } from '../ui/thought-bubble'
import styles from './message-node.module.css'

// Helper function to strip think tags from content
function stripThinkTags(content: string): string {
  // If there is no <think> tag, return the original content.
  if (!content.includes("<think>")) return content;
  // If a closing tag exists, remove all complete <think>...</think> segments.
  if (content.includes("</think>")) {
    return content.replace(/<think>[\s\S]*?<\/think>/g, '');
  }
  // Otherwise (incomplete <think> tag), remove text starting at the first "<think>"
  return content.split("<think>")[0];
}

interface MessageNodeProps {
  data: {
    message: {
      id: string
      content: string
      role: string
      model: string | null
      metadata: {
        streaming?: boolean
        error?: boolean
        loadedData?: {
          count: number
          items: any[]
        }
      }
      children?: any[]
      context?: { [key: string]: string }[]
    }
    onClick?: () => void
    onRepath?: () => void
    isSelected?: boolean
    hasUserDescendants?: boolean
  }
  selected: boolean
  draggable?: boolean
}

export function MessageNode({
  data,
  selected,
  draggable = false,
}: MessageNodeProps) {
  const [fullContentDialogOpen, setFullContentDialogOpen] = useState(false)
  const [dataDialogOpen, setDataDialogOpen] = useState(false)
  const [fileDialogOpen, setFileDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState<string>('')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [showThoughts, setShowThoughts] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const { message, onClick: onClickProp, onRepath: onRepathProp, hasUserDescendants } = data
  const { role, content: fullContent = '', model, metadata = {}, context = [] } = message || {}
  const isStreaming = metadata?.streaming ?? false
  const loadedData = metadata?.loadedData

  // Check for thoughts in the content
  const hasThoughts = fullContent.includes('<think>')

  // Use stripThinkTags to robustly handle think tag removal
  const originalContent = stripThinkTags(fullContent).trim();
  // If there is loaded data, append its content below the main text
  const combinedContent = loadedData
    ? originalContent + "\n\n" + loadedData.items.map(item => item.content).join("\n\n")
    : originalContent

  // Determine if it's a user message first
  const isUser = role === 'user'
  const displayName = isUser 
    ? 'You' 
    : role === 'system'
      ? 'System'
      : (model ? getModelIconAndName(model).name : 'Assistant')

  // Get model info or use appropriate defaults based on role
  const { icon: displayedIcon } = isUser 
    ? { icon: '👤' }  // User icon
    : role === 'system'
      ? { icon: '⚙️' }  // System icon
      : (model 
          ? getModelIconAndName(model) 
          : { icon: '🤖' }  // Default assistant icon
        )

  const toggleDrawer = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent event from bubbling up
    setIsDrawerOpen(!isDrawerOpen)
  }

  const handleFileClick = (e: React.MouseEvent, filename: string, content: string) => {
    e.stopPropagation() // Prevent event from bubbling up
    setSelectedFile(filename)
    setFileContent(content)
    setFileDialogOpen(true)
  }

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const hasOverflow = contentRef.current.scrollHeight > contentRef.current.clientHeight
        setIsOverflowing(hasOverflow)
      }
    }
    
    checkOverflow()
    // Re-check on window resize
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [combinedContent]) // Re-check when content changes

  return (
    <div className="relative">
      <div
        className={cn(
          "group relative w-[400px]",
          styles.messageNode,
          selected && "ring-2 ring-primary rounded-xl",
          isUser && "hover:ring-2 hover:ring-primary/20 rounded-xl",
        )}
      >
        {/* Top Handle */}
        <Handle
          type="target"
          position={Position.Top}
          className={cn(
            "h-3 w-3 rounded-full border-2 border-white dark:border-gray-800",
            "transition-colors duration-200 ease-in-out",
            selected ? "bg-primary shadow-[0_0_8px_rgba(0,0,0,0.2)]" : "bg-muted-foreground/50"
          )}
          isConnectable={false}
        />

        <Card
          onClick={onClickProp}
          className={cn(
            "p-4 w-full max-h-[300px] cursor-pointer relative overflow-hidden",
            "transition-all duration-200 ease-in-out",
            "rounded-xl shadow-md hover:shadow-lg",
            "bg-white/50 dark:bg-gray-800/50 backdrop-blur-md",
            selected
              ? "ring-2 ring-offset-2 ring-offset-background ring-primary scale-[1.03] shadow-lg"
              : "hover:scale-[1.01]",
            loadedData && styles.messageNodeDataLoaded
          )}
          style={{
            backgroundImage: selected
              ? "linear-gradient(to bottom right, rgba(0,0,0,0.02), rgba(0,0,0,0.02)), radial-gradient(circle at top left, rgba(56,189,248,0.3), rgba(99,102,241,0.3))"
              : loadedData 
                ? "linear-gradient(135deg, rgba(var(--primary), 0.05) 25%, transparent 25%), linear-gradient(225deg, rgba(var(--primary), 0.05) 25%, transparent 25%), linear-gradient(45deg, rgba(var(--primary), 0.05) 25%, transparent 25%), linear-gradient(315deg, rgba(var(--primary), 0.05) 25%, transparent 25%)"
                : undefined,
            backgroundSize: loadedData ? "10px 10px" : undefined,
            backgroundPosition: loadedData ? "0 0, 5px 0, 5px -5px, 0px 5px" : undefined,
            backgroundBlendMode: "soft-light",
          }}
          draggable={draggable}
        >
          {/* Header with Avatar and Model Name */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <div className="text-sm">{displayedIcon}</div>
              </Avatar>
              <span className="text-sm font-medium">{displayName}</span>
            </div>
            <div className="flex items-center gap-1">
              {hasThoughts && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowThoughts(!showThoughts)
                  }}
                  className={cn(
                    "p-1.5 rounded-lg",
                    "bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm",
                    "border border-white/20 dark:border-gray-700/30",
                    "shadow-sm hover:shadow-md transition-all",
                    "hover:scale-105 active:scale-95",
                    showThoughts && "bg-white/70 dark:bg-gray-800/70"
                  )}
                >
                  <Brain className="h-4 w-4" />
                </button>
              )}
              {!isUser && onRepathProp && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onRepathProp()
                  }}
                  className={cn(
                    "p-1.5 rounded-lg",
                    "bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm",
                    "border border-white/20 dark:border-gray-700/30",
                    "shadow-sm hover:shadow-md transition-all",
                    "hover:scale-105 active:scale-95"
                  )}
                >
                  <RotateCw className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setFullContentDialogOpen(true)
                }}
                className={cn(
                  "p-1.5 rounded-lg",
                  "bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm",
                  "border border-white/20 dark:border-gray-700/30",
                  "shadow-sm hover:shadow-md transition-all",
                  "hover:scale-105 active:scale-95"
                )}
              >
                <Maximize2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content Area with Truncation */}
          <div className="relative">
            <div 
              ref={contentRef}
              className={cn(
                "prose prose-sm dark:prose-invert max-w-none max-h-[200px] overflow-hidden"
              )}
            >
              <div className="break-words whitespace-pre-wrap leading-relaxed">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {combinedContent}
                </ReactMarkdown>
              </div>
            </div>
            {isOverflowing && (
              <div className={cn(
                "absolute bottom-0 left-0 right-0 h-12 pointer-events-none",
                "bg-gradient-to-t to-transparent",
                selected 
                  ? "from-[rgba(56,189,248,0.03)]" 
                  : "from-white dark:from-gray-800"
              )} />
            )}
          </div>

          {/* Loading indicator */}
          {isStreaming && (
            <div className="absolute top-2 right-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
        </Card>

        {/* Bottom Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          className={cn(
            "h-3 w-3 rounded-full border-2 border-white dark:border-gray-800",
            "transition-colors duration-200 ease-in-out",
            selected ? "bg-primary shadow-[0_0_8px_rgba(0,0,0,0.2)]" : "bg-muted-foreground/50"
          )}
          isConnectable={false}
        />
      </div>

      {/* Full content dialog */}
      <MessageDialog
        open={fullContentDialogOpen}
        onOpenChange={setFullContentDialogOpen}
        role={role}
        model={model}
        content={combinedContent}
      />
      {showThoughts && hasThoughts && (
        <div className="absolute top-full left-0 right-0 mt-2 z-50">
          <ThoughtBubble content={fullContent} />
        </div>
      )}
    </div>
  )
}
